import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { isNoonSalesImportActive, useNoonSalesImportJob } from '../channels/noonSalesImport/useNoonSalesImportJob'
import type { NoonSalesImportCapability, NoonSalesImportJob } from '../channels/noonSalesImport/types'
import type { ChannelConnection } from '../channels/types'
import { Button, Card, ChannelDot, I, Pill, TEXT_WRAP_STYLE, gridColumns, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

interface NoonSalesImportPanelProps {
  accessToken: string
  connection: ChannelConnection
  capability: NoonSalesImportCapability
  onClose: () => void
  onJobCreated?: (job: NoonSalesImportJob) => void
}

const FULFILLMENT_KEYS: Record<string, string> = {
  'Fulfilled by Noon (FBN)': 'fbn',
  Supermall: 'supermall',
  'Fulfilled by Partner (FBP)': 'fbp',
}

function statusTone(job: NoonSalesImportJob): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (job.status === 'COMPLETED') return 'success'
  if (job.status === 'COMPLETED_WITH_ISSUES') return 'warning'
  if (job.status === 'FAILED') return 'danger'
  if (job.status === 'CANCELLED') return 'neutral'
  return 'info'
}

function errorKey(error: unknown): string {
  return error instanceof ApiClientError
    ? `channels.noonSalesImport.errors.${error.code}`
    : 'channels.noonSalesImport.errors.REQUEST_FAILED'
}

function normalizedExtension(extension: string): string {
  const normalized = extension.trim().toLowerCase()
  return normalized.startsWith('.') ? normalized : `.${normalized}`
}

export function NoonSalesImportPanel({ accessToken, connection, capability, onClose, onJobCreated }: NoonSalesImportPanelProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [file, setFile] = useState<File | null>(null)
  const [sourceTimeZone, setSourceTimeZone] = useState('Asia/Riyadh')
  const [partnerId, setPartnerId] = useState(connection.noonSalesPartnerId ?? '')
  const [partnerSaved, setPartnerSaved] = useState(Boolean(connection.noonSalesPartnerId))
  const [validationError, setValidationError] = useState<unknown>(null)
  const {
    job, rowPage, loadingLatest, submitting, cancelling, error, pollWarning,
    submit: submitJob, cancel, setPage,
  } = useNoonSalesImportJob(accessToken, connection.id)
  const mobileActionStyle = mobileButtonStyle(isMobile)
  const extensions = capability.acceptedFileExtensions.map(normalizedExtension).filter(Boolean)
  const configurationInvalid = extensions.length === 0 || capability.acceptedFulfillmentModels.length === 0
  const visibleError = validationError ?? error

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setValidationError(null)
    if (configurationInvalid) {
      setValidationError(new ApiClientError(409, { code: 'NOON_SALES_IMPORT_CONFIGURATION_INVALID' }))
      return
    }
    if (!file) {
      setValidationError(new ApiClientError(400, { code: 'NOON_SALES_IMPORT_FILE_REQUIRED' }))
      return
    }
    if (!extensions.some(extension => file.name.toLowerCase().endsWith(extension))) {
      setValidationError(new ApiClientError(400, { code: 'NOON_SALES_IMPORT_FILE_TYPE_INVALID' }))
      return
    }
    if (file.size > capability.maxFileBytes) {
      setValidationError(new ApiClientError(400, { code: 'NOON_SALES_IMPORT_FILE_TOO_LARGE' }))
      return
    }
    if (capability.sourceTimeZoneRequired && !sourceTimeZone.trim()) {
      setValidationError(new ApiClientError(400, { code: 'NOON_SALES_IMPORT_TIMEZONE_REQUIRED' }))
      return
    }
    const needsPartner = connection.status !== 'CONNECTED' && !partnerSaved
    if (needsPartner && !/^[0-9]{1,64}$/.test(partnerId.trim())) {
      setValidationError(new ApiClientError(400, { code: 'NOON_SALES_IMPORT_PARTNER_REQUIRED' }))
      return
    }
    const zone = capability.sourceTimeZoneRequired ? sourceTimeZone.trim() : undefined
    const created = needsPartner ? await submitJob(file, zone, partnerId.trim()) : await submitJob(file, zone)
    if (created) {
      setPartnerSaved(true)
      onJobCreated?.(created)
    }
  }

  const matchedCount = job ? job.matchedOrderUpdatedCount + job.matchedOrderUnchangedCount : 0
  const standaloneCount = job
    ? job.standaloneCreatedCount + job.standaloneUpdatedCount + job.standaloneUnchangedCount + job.standaloneSupersededCount
    : 0

  return (
    <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--brand-300)' }}>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <ChannelDot name="Noon" size={30} />
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>
              {t('channels.noonSalesImport.title', { name: connection.displayName })}
            </div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55, maxWidth: 760, ...TEXT_WRAP_STYLE }}>
            {t('channels.noonSalesImport.description')}
          </p>
        </div>
        <Button kind="ghost" size="xs" type="button" onClick={onClose} style={mobileActionStyle}>
          {t('channels.noonSalesImport.close')}
        </Button>
      </div>

      <div style={{ display: 'grid', gap: 8, marginTop: 16, padding: 14, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('channels.noonSalesImport.fulfillmentTitle')}</div>
        <ul style={{ margin: 0, paddingInlineStart: 20, fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
          {capability.acceptedFulfillmentModels.map(model => (
            <li key={model}>{FULFILLMENT_KEYS[model] ? t(`channels.noonSalesImport.fulfillment.${FULFILLMENT_KEYS[model]}`) : model}</li>
          ))}
        </ul>
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{t('channels.noonSalesImport.matching')}</div>
        <div role="alert" style={{ fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.5, fontWeight: 700 }}>{t('channels.noonSalesImport.chronologicalWarning')}</div>
      </div>

      <form onSubmit={submit} style={{ marginTop: 16 }}>
        {(connection.status !== 'CONNECTED' || connection.noonSalesPartnerId) && (
          <label htmlFor="noon-sales-import-partner" style={{ display: 'block', marginBottom: 14, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.noonSalesImport.partnerLabel')}</div>
            <input id="noon-sales-import-partner" value={partnerId} onChange={event => setPartnerId(event.target.value)}
              inputMode="numeric" maxLength={64} readOnly={partnerSaved}
              style={{ width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 9 }} />
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 5, lineHeight: 1.5 }}>{t('channels.noonSalesImport.partnerHint')}</div>
          </label>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) minmax(220px, 0.7fr)'), gap: 14, alignItems: 'end' }}>
          <label htmlFor="noon-sales-import-file" style={{ display: 'block', minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.noonSalesImport.fileLabel')}</div>
            <input
              id="noon-sales-import-file"
              type="file"
              accept={extensions.join(',')}
              onChange={event => setFile(event.target.files?.[0] ?? null)}
              style={{ width: '100%', minWidth: 0, paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 9 }}
            />
            <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 5 }}>
              {t('channels.noonSalesImport.fileHint', { extensions: extensions.join(', '), size: Math.floor(capability.maxFileBytes / 1024 / 1024), rows: capability.maxRows })}
            </div>
          </label>
          {capability.sourceTimeZoneRequired && (
            <label htmlFor="noon-sales-import-time-zone" style={{ display: 'block', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.noonSalesImport.timeZoneLabel')}</div>
              <input
                id="noon-sales-import-time-zone"
                value={sourceTimeZone}
                onChange={event => setSourceTimeZone(event.target.value)}
                placeholder={t('channels.noonSalesImport.timeZonePlaceholder')}
                required
                style={{ width: '100%', minWidth: 0, paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 9 }}
              />
              <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 5 }}>{t('channels.noonSalesImport.timeZoneHint')}</div>
            </label>
          )}
        </div>
        <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
          {job && isNoonSalesImportActive(job) && (
            <Button kind="danger" size="sm" type="button" onClick={() => void cancel()} disabled={cancelling} style={mobileActionStyle}>
              {cancelling ? t('channels.noonSalesImport.cancelling') : t('channels.noonSalesImport.cancel')}
            </Button>
          )}
          <Button kind="primary" size="sm" type="submit" icon={I.download} disabled={configurationInvalid || submitting || Boolean(job && isNoonSalesImportActive(job))} style={mobileActionStyle}>
            {submitting
              ? t('channels.noonSalesImport.uploading')
              : job?.status === 'FAILED'
                ? t('channels.noonSalesImport.reupload')
                : t('channels.noonSalesImport.upload')}
          </Button>
        </div>
      </form>

      {visibleError !== null && (
        <div role="alert" style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, ...TEXT_WRAP_STYLE }}>
          {t(errorKey(visibleError), { defaultValue: t('channels.noonSalesImport.errors.REQUEST_FAILED') })}
        </div>
      )}
      {configurationInvalid && visibleError === null && (
        <div role="alert" style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, ...TEXT_WRAP_STYLE }}>
          {t('channels.noonSalesImport.errors.NOON_SALES_IMPORT_CONFIGURATION_INVALID')}
        </div>
      )}
      {pollWarning !== null && (
        <div role="status" style={{ marginTop: 12, color: 'var(--warning)', fontSize: 12.5, fontWeight: 700, ...TEXT_WRAP_STYLE }}>
          {t('channels.noonSalesImport.pollWarning')}
        </div>
      )}

      {loadingLatest && <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--fg-muted)' }}>{t('channels.noonSalesImport.loadingLatest')}</div>}

      {job && (
        <div style={{ display: 'grid', gap: 14, marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('channels.noonSalesImport.jobTitle', { id: job.id })}</div>
            <Pill tone={statusTone(job)} size="sm" dot>{t(`channels.noonSalesImport.status.${job.status}`)}</Pill>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(120px, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            {([
              ['processed', job.processedRowCount],
              ['matched', matchedCount],
              ['standalone', standaloneCount],
              ['issues', job.totalIssueCount],
            ] as const).map(([key, value]) => (
              <div key={key} style={{ padding: 12, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)', minWidth: 0 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)' }}>{value}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, ...TEXT_WRAP_STYLE }}>{t(`channels.noonSalesImport.counts.${key}`)}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>
            {t('channels.noonSalesImport.acceptedCounts', { fbn: job.acceptedFbnRowCount, fbp: job.acceptedFbpRowCount })}
          </div>
          {job.errorCode && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
              {t(`channels.noonSalesImport.errors.${job.errorCode}`, { defaultValue: t('channels.noonSalesImport.errors.REQUEST_FAILED') })}
            </div>
          )}
          {job.issueSummaries.length > 0 && (
            <div style={{ display: 'grid', gap: 7 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('channels.noonSalesImport.issueSummaryTitle')}</div>
              {job.issueSummaries.map(issue => (
                <div key={`${issue.code}-${issue.firstRowNumber}`} style={{ fontSize: 12.5, color: 'var(--warning)', ...TEXT_WRAP_STYLE }}>
                  {t(`channels.noonSalesImport.issueCodes.${issue.code}`, { defaultValue: t('channels.noonSalesImport.issueCodes.UNKNOWN') })}
                  {' · '}{t('channels.noonSalesImport.issueContext', { count: issue.count, row: issue.firstRowNumber })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {rowPage && rowPage.items.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 10 }}>{t('channels.noonSalesImport.rowsTitle')}</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {rowPage.items.map(row => (
              <div key={row.id} style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, '70px minmax(110px, 0.7fr) minmax(140px, 1fr) minmax(180px, 1.4fr)'), gap: 10, alignItems: 'start', padding: 11, borderRadius: 10, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)', minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{t('channels.noonSalesImport.rowNumber', { row: row.rowNumber })}</div>
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{row.itemReference ?? t('channels.noonSalesImport.unknownItem')}</div>
                <div><Pill tone={row.outcome === 'FAILED' || row.outcome === 'CONFLICT' ? 'danger' : row.outcome === 'SKIPPED' ? 'warning' : row.outcome === 'PENDING' ? 'info' : 'success'} size="sm">{t(`channels.noonSalesImport.outcomes.${row.outcome}`)}</Pill></div>
                <div style={{ display: 'grid', gap: 3, fontSize: 12, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>
                  {row.issues.length === 0
                    ? t('channels.noonSalesImport.noRowIssues')
                    : row.issues.map(issue => (
                        <span key={`${issue.code}-${issue.column ?? ''}`}>
                          {t(`channels.noonSalesImport.issueCodes.${issue.code}`, { defaultValue: t('channels.noonSalesImport.issueCodes.UNKNOWN') })}
                          {issue.column ? ` · ${issue.column}` : ''}
                        </span>
                      ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{t('channels.noonSalesImport.page', { page: rowPage.page + 1, pages: rowPage.totalPages })}</div>
            <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8 }}>
              <Button kind="secondary" size="xs" type="button" onClick={() => setPage(current => Math.max(0, current - 1))} disabled={rowPage.page === 0} style={mobileActionStyle}>{t('channels.noonSalesImport.previous')}</Button>
              <Button kind="secondary" size="xs" type="button" onClick={() => setPage(current => current + 1)} disabled={rowPage.page + 1 >= rowPage.totalPages} style={mobileActionStyle}>{t('channels.noonSalesImport.next')}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
